import React, { useMemo, useState } from 'react';
import { Shield, ShieldCheck, Key, Smartphone, LogOut, AlertTriangle, CheckCircle2, XCircle, Eye, EyeOff, Monitor, Bell, Download, Copy, Lock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
interface SecurityTabProps {
  theme: ZodiacTheme;
  user: User | null;
}
const BACKUP_CODES = ['A7K2-M9X1', 'B3P8-N4Q6', 'C5R1-L7W3', 'D9T4-J2Y8', 'E1V6-H5Z0', 'F8U3-G6S7'];
const SESSIONS = [{
  id: 1,
  device: 'Mac OS',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '192.168.1.1',
  time: 'Сейчас',
  current: true,
  icon: 'monitor' as const
}, {
  id: 2,
  device: 'iPhone 13',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '10.0.0.5',
  time: 'Вчера, 14:30',
  current: false,
  icon: 'phone' as const
}, {
  id: 3,
  device: 'Windows 11',
  browser: 'Chrome',
  city: 'Санкт-Петербург',
  country: 'Россия',
  ip: '85.12.45.67',
  time: '3 дня назад',
  current: false,
  icon: 'monitor' as const
}];
function getPasswordStrength(pw: string, t: any): {
  level: number;
  label: string;
  color: string;
} {
  if (!pw) return {
    level: 0,
    label: '',
    color: ''
  };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-zа-я]/.test(pw) && /[A-ZА-Я]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Zа-яА-Я0-9]/.test(pw)) score++;
  if (score <= 1) return {
    level: 1,
    label: t('userdash.security.weak'),
    color: 'bg-rose-500'
  };
  if (score === 2) return {
    level: 2,
    label: t('userdash.security.medium'),
    color: 'bg-amber-500'
  };
  if (score === 3) return {
    level: 3,
    label: t('userdash.security.good'),
    color: 'bg-emerald-400'
  };
  return {
    level: 4,
    label: t('userdash.security.strong'),
    color: 'bg-emerald-600'
  };
}
export function SecurityTab({
  theme,
  user
}: SecurityTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  // Password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  // 2FA
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState<'sms' | 'app' | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  // Sessions
  const [sessions, setSessions] = useState(SESSIONS);
  // Notifications
  const [notifyLogin, setNotifyLogin] = useState(true);
  const [notifyPasswordChange, setNotifyPasswordChange] = useState(true);
  const pwStrength = useMemo(() => getPasswordStrength(newPw, t), [newPw, t]);
  const pwChecks = useMemo(() => ({
    length: newPw.length >= 8,
    letters: /[a-zA-Zа-яА-Я]/.test(newPw),
    digits: /\d/.test(newPw),
    match: newPw.length > 0 && newPw === confirmPw
  }), [newPw, confirmPw]);
  // Security score
  const securityScore = useMemo(() => {
    let score = 0;
    const items = [{
      label: t('userdash.security.passwordUpdated'),
      ok: true
    }, {
      label: t('userdash.security.2faEnabled'),
      ok: is2FAEnabled
    }, {
      label: t('userdash.security.emailVerified'),
      ok: true
    }, {
      label: t('userdash.security.phoneVerified'),
      ok: false
    }];
    items.forEach(i => {
      if (i.ok) score++;
    });
    return {
      score,
      total: items.length,
      items
    };
  }, [is2FAEnabled]);
  const scorePercent = securityScore.score / securityScore.total * 100;
  const scoreColor = scorePercent >= 75 ? 'text-emerald-600' : scorePercent >= 50 ? 'text-amber-600' : 'text-rose-600';
  const scoreBg = scorePercent >= 75 ? 'bg-emerald-500' : scorePercent >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  const handleChangePassword = () => {
    if (!currentPw) {
      showToast(t('userdash.security.enterCurrentPw'));
      return;
    }
    if (!pwChecks.length || !pwChecks.letters || !pwChecks.digits) {
      showToast(t('userdash.security.pwRequirements'));
      return;
    }
    if (!pwChecks.match) {
      showToast(t('userdash.security.pwMismatch'));
      return;
    }
    showToast(t('userdash.security.pwUpdated'));
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
  };
  const handleEnable2FA = (method: 'sms' | 'app') => {
    setTwoFAMethod(method);
    setIs2FAEnabled(true);
    setShow2FASetup(false);
    showToast(t('userdash.security.2faEnabledVia', {
      method: method === 'sms' ? 'SMS' : 'Google Authenticator'
    }));
  };
  const handleEndSession = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast(t('userdash.security.sessionEnded'));
  };
  const handleNotMe = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast(t('userdash.security.sessionEndedNotMe'));
  };
  const handleEndAllSessions = () => {
    setSessions(sessions.filter(s => s.current));
    showToast(t('userdash.security.allSessionsEnded'));
  };
  const Toggle = ({
    checked,
    onChange
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => <button onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-19263">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-19264" />
    </button>;
  const DeviceIcon = ({
    type
  }: {
    type: 'monitor' | 'phone';
  }) => type === 'monitor' ? <Monitor className="w-4 h-4" data-id="element-19265" /> : <Smartphone className="w-4 h-4" data-id="element-19266" />;
  return <div className="space-y-5 max-w-3xl pb-8" data-id="element-19267">
      {/* Header */}
      <div data-id="element-19268">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19269">
          {t('userdash.security.title')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-19270">
          {t('userdash.security.subtitle')}
        </p>
      </div>

      {/* Security Score */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-id="element-19271">
        <div className="flex items-center justify-between mb-3" data-id="element-19272">
          <div className="flex items-center gap-2" data-id="element-19273">
            {scorePercent >= 75 ? <ShieldCheck className="w-5 h-5 text-emerald-600" data-id="element-19274" /> : <Shield className="w-5 h-5 text-amber-500" data-id="element-19275" />}
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-19276">
              {t('userdash.security.status')}
            </h3>
          </div>
          <span className={`text-lg font-bold ${scoreColor}`} data-id="element-19277">
            {securityScore.score}/{securityScore.total}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 mb-3" data-id="element-19278">
          <div className={`h-2 rounded-full transition-all ${scoreBg}`} style={{
          width: `${scorePercent}%`
        }} data-id="element-19279" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-19280">
          {securityScore.items.map((item, i) => <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${item.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-19281">
              {item.ok ? <CheckCircle2 className="w-3 h-3 shrink-0" data-id="element-19282" /> : <XCircle className="w-3 h-3 shrink-0" data-id="element-19283" />}
              <span className="truncate" data-id="element-19284">{item.label}</span>
            </div>)}
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19285">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19286">
          <Key className="w-4 h-4 text-slate-400" data-id="element-19287" />{' '}
          {t('userdash.security.changePassword')}
        </h3>

        <div data-id="element-19288">
          <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19289">
            {t('userdash.security.currentPassword')}
          </label>
          <div className="relative" data-id="element-19290">
            <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19291" />
            <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-19292">
              {showCurrentPw ? <EyeOff className="w-4 h-4" data-id="element-19293" /> : <Eye className="w-4 h-4" data-id="element-19294" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-19295">
          <div data-id="element-19296">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19297">
              {t('userdash.security.newPassword')}
            </label>
            <div className="relative" data-id="element-19298">
              <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19299" />
              <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-19300">
                {showNewPw ? <EyeOff className="w-4 h-4" data-id="element-19301" /> : <Eye className="w-4 h-4" data-id="element-19302" />}
              </button>
            </div>
          </div>
          <div data-id="element-19303">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19304">
              {t('userdash.security.confirmPassword')}
            </label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19305" />
          </div>
        </div>

        {/* Strength indicator */}
        {newPw && <div className="space-y-2" data-id="element-19306">
            <div className="flex items-center gap-2" data-id="element-19307">
              <div className="flex gap-1 flex-1" data-id="element-19308">
                {[1, 2, 3, 4].map(lvl => <div key={lvl} className={`h-1.5 flex-1 rounded-full transition-colors ${lvl <= pwStrength.level ? pwStrength.color : 'bg-slate-100'}`} data-id="element-19309" />)}
              </div>
              <span className={`text-[10px] font-medium ${pwStrength.level <= 1 ? 'text-rose-600' : pwStrength.level === 2 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-19310">
                {pwStrength.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-19311">
              {[{
            ok: pwChecks.length,
            label: t('userdash.security.minChars')
          }, {
            ok: pwChecks.letters,
            label: t('userdash.security.letters')
          }, {
            ok: pwChecks.digits,
            label: t('userdash.security.digits')
          }, {
            ok: pwChecks.match,
            label: t('userdash.security.match')
          }].map((c, i) => <span key={i} className={`text-[10px] flex items-center gap-0.5 ${c.ok ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-19312">
                  {c.ok ? <CheckCircle2 className="w-3 h-3" data-id="element-19313" /> : <XCircle className="w-3 h-3" data-id="element-19314" />}{' '}
                  {c.label}
                </span>)}
            </div>
          </div>}

        <Button size="sm" className="text-xs" onClick={handleChangePassword} disabled={!currentPw || !pwChecks.length || !pwChecks.digits || !pwChecks.match} data-id="element-19315">
          {t('userdash.security.updatePassword')}
        </Button>
      </div>

      {/* 2FA */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19316">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19317">
          <Shield className="w-4 h-4 text-slate-400" data-id="element-19318" />{' '}
          {t('userdash.security.twoFactor')}
        </h3>

        <div className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${is2FAEnabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-id="element-19319">
          <div data-id="element-19320">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-19321">
              {is2FAEnabled ? t('userdash.security.enabled') : t('userdash.security.disabled')}
              {is2FAEnabled && twoFAMethod && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-19322">
                  {twoFAMethod === 'sms' ? 'SMS' : 'Authenticator'}
                </Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-id="element-19323">
              {t('userdash.security.twoFactorDesc')}
            </div>
          </div>
          {is2FAEnabled ? <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => {
          setIs2FAEnabled(false);
          setTwoFAMethod(null);
          showToast(t('userdash.security.2faDisabled'));
        }} data-id="element-19324">
              {t('userdash.security.disable')}
            </Button> : <Button size="sm" className="text-xs shrink-0" onClick={() => setShow2FASetup(true)} data-id="element-19325">
              {t('userdash.security.enable')}
            </Button>}
        </div>

        {/* 2FA Setup */}
        {show2FASetup && !is2FAEnabled && <div className="space-y-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-19326">
            <div className="text-xs font-medium text-indigo-900" data-id="element-19327">
              {t('userdash.security.chooseMethod')}:
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-19328">
              <button onClick={() => handleEnable2FA('sms')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-19329">
                <Smartphone className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19330" />
                <div data-id="element-19331">
                  <div className="text-xs font-medium text-slate-900" data-id="element-19332">SMS</div>
                  <div className="text-[10px] text-slate-500" data-id="element-19333">
                    {t('userdash.security.smsDesc')}
                  </div>
                </div>
              </button>
              <button onClick={() => handleEnable2FA('app')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-19334">
                <Lock className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19335" />
                <div data-id="element-19336">
                  <div className="text-xs font-medium text-slate-900" data-id="element-19337">
                    Authenticator
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-19338">
                    Google / Authy
                  </div>
                </div>
              </button>
            </div>
          </div>}

        {/* Backup Codes */}
        {is2FAEnabled && <div data-id="element-19339">
            <button onClick={() => setShowBackupCodes(!showBackupCodes)} className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1" data-id="element-19340">
              <Key className="w-3 h-3" data-id="element-19341" />{' '}
              {showBackupCodes ? t('userdash.security.hide') : t('userdash.security.show')}{' '}
              {t('userdash.security.backupCodes')}
            </button>
            {showBackupCodes && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-id="element-19342">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-id="element-19343">
                  {BACKUP_CODES.map(code => <div key={code} className="text-xs font-mono text-slate-700 bg-white px-2 py-1.5 rounded border border-slate-200 text-center" data-id="element-19344">
                      {code}
                    </div>)}
                </div>
                <div className="flex gap-2" data-id="element-19345">
                  <button onClick={() => {
              navigator.clipboard?.writeText(BACKUP_CODES.join('\n'));
              showToast(t('userdash.security.codesCopied'));
            }} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-19346">
                    <Copy className="w-3 h-3" data-id="element-19347" /> {t('userdash.security.copy')}
                  </button>
                  <button onClick={() => showToast(t('userdash.security.codesDownloaded'))} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-19348">
                    <Download className="w-3 h-3" data-id="element-19349" />{' '}
                    {t('userdash.security.download')}
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      {/* Active Sessions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19350">
        <div className="flex items-center justify-between" data-id="element-19351">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19352">
            <Monitor className="w-4 h-4 text-slate-400" data-id="element-19353" />{' '}
            {t('userdash.security.activeSessions')}
          </h3>
          {sessions.filter(s => !s.current).length > 0 && <Button variant="outline" size="sm" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50 font-medium" onClick={handleEndAllSessions} data-id="element-19354">
              <LogOut className="w-3.5 h-3.5 mr-1" data-id="element-19355" />{' '}
              {t('userdash.security.endAllSessions')}
            </Button>}
        </div>

        <div className="space-y-2" data-id="element-19356">
          {sessions.map(s => <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border ${s.current ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200'}`} data-id="element-19357">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.current ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-19358">
                <DeviceIcon type={s.icon} data-id="element-19359" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-19360">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-19361">
                  {s.device} · {s.browser}
                  {s.current && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-19362">
                      {t('userdash.security.currentSession')}
                    </Badge>}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19363">
                  {s.city}, {s.country} · IP: {s.ip} · {s.time}
                </div>
              </div>
              {!s.current && <div className="flex items-center gap-1 shrink-0" data-id="element-19364">
                  <button onClick={() => handleEndSession(s.id)} className="text-[10px] text-slate-500 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50 transition-colors" data-id="element-19365">
                    {t('userdash.security.endSession')}
                  </button>
                  <button onClick={() => handleNotMe(s.id)} className="text-[10px] text-rose-600 hover:text-rose-700 px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 font-medium transition-colors" data-id="element-19366">
                    {t('userdash.security.notMe')}
                  </button>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Security Notifications */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-3" data-id="element-19367">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19368">
          <Bell className="w-4 h-4 text-slate-400" data-id="element-19369" />{' '}
          {t('userdash.security.notifications')}
        </h3>
        <div className="divide-y divide-slate-100" data-id="element-19370">
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-19371">
            <div data-id="element-19372">
              <div className="text-sm font-medium text-slate-900" data-id="element-19373">
                {t('userdash.security.loginAlert')}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-19374">
                {t('userdash.security.loginAlertDesc')}
              </div>
            </div>
            <Toggle checked={notifyLogin} onChange={setNotifyLogin} data-id="element-19375" />
          </div>
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-19376">
            <div data-id="element-19377">
              <div className="text-sm font-medium text-slate-900" data-id="element-19378">
                {t('userdash.security.passwordAlert')}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-19379">
                {t('userdash.security.passwordAlertDesc')}
              </div>
            </div>
            <Toggle checked={notifyPasswordChange} onChange={setNotifyPasswordChange} data-id="element-19380" />
          </div>
        </div>
      </div>
    </div>;
}